/**
 * ZipcodeMapPage - 邮编地图电子围栏（黑名单）页面
 *
 * 功能：按 zipcode 展示地图边界与电子围栏列表，支持绘制/保存/审核/删除围栏、
 * 地址搜索定位、全屏、帮助说明等。地图边界由接口获取，围栏支持与边界裁剪及与已有围栏差集。
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Polygon, Polyline, Marker } from '@react-google-maps/api';
import { Spin, Button, message, Form, Input, Select, Table, Modal, Checkbox } from 'antd';
import { ArrowLeftOutlined, EditOutlined, FullscreenOutlined, FullscreenExitOutlined, ClearOutlined, LeftOutlined, RightOutlined, ZoomInOutlined, VerticalAlignMiddleOutlined, BorderOutlined, QuestionCircleOutlined, AimOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation } from '@apollo/client';
import * as turf from '@turf/turf';
import { GET_ZIPCODE_MAP_BOUNDARY, GetZipcodeMapBoundaryResponse } from '../graphql/queries/getZipcodeMapBoundary';
import { GET_ZIPCODE_GEOFENCE_LISTING, GetZipcodeGeofenceListingResponse, ZipcodeMapGeofenceType } from '../graphql/queries/getZipcodeGeofenceListing';
import { ADD_ZIPCODE_GEOFENCE, AddZipcodeGeofenceResponse } from '../graphql/mutations/addZipcodeGeofence';
import { AUDIT_ZIPCODE_GEOFENCE, AuditZipcodeGeofenceResponse } from '../graphql/mutations/auditZipcodeGeofence';
import { DELETE_ZIPCODE_GEOFENCE, DeleteZipcodeGeofenceResponse } from '../graphql/mutations/deleteZipcodeGeofence';
import { getUserInfo } from '../../../../utils/auth';
import { ResourceCode } from '../../../../constants/resourceCodes';
import {formatTimestampToDateTime} from "../../../../utils/dateTimeUtils";
import { buildGeofenceColumns, getDescriptionLabel, getGeofenceStatusConfig } from './GeofenceColumns';

const GOOGLE_MAPS_API_KEY = 'AIzaSyD8huYA1dL8uG9O1a77bnW3JtpTjUimjmc';

/** 节流：限制地图鼠标事件的触发频率 */
function throttle<T extends (e: google.maps.MapMouseEvent) => void>(func: T, delay: number): T {
    let lastCall = 0;
    return ((e: google.maps.MapMouseEvent) => {
        const now = Date.now();
        if (now - lastCall >= delay) {
            lastCall = now;
            func(e);
        }
    }) as T;
}

/** 射线法判断点是否在多边形内部 */
function isPointInPolygon(point: google.maps.LatLngLiteral, polygon: google.maps.LatLngLiteral[]): boolean {
    let inside = false;
    const x = point.lng;
    const y = point.lat;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].lng;
        const yi = polygon[i].lat;
        const xj = polygon[j].lng;
        const yj = polygon[j].lat;
        const intersect = ((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
        if (intersect) inside = !inside;
    }
    return inside;
}

/** 求点到线段的最近点（投影或端点） */
function nearestPointOnSegment(
    p: google.maps.LatLngLiteral,
    a: google.maps.LatLngLiteral,
    b: google.maps.LatLngLiteral
): google.maps.LatLngLiteral {
    const dx = b.lng - a.lng;
    const dy = b.lat - a.lat;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return a;
    let t = ((p.lng - a.lng) * dx + (p.lat - a.lat) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return { lat: a.lat + t * dy, lng: a.lng + t * dx };
}

/** 求点到边界多边形的最近点（遍历所有边） */
function nearestPointOnBoundary(
    point: google.maps.LatLngLiteral,
    boundaryPaths: google.maps.LatLngLiteral[][]
): google.maps.LatLngLiteral {
    let nearest = point;
    let minDist = Infinity;
    for (const path of boundaryPaths) {
        for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
            const proj = nearestPointOnSegment(point, path[j], path[i]);
            const d = (point.lat - proj.lat) ** 2 + (point.lng - proj.lng) ** 2;
            if (d < minDist) {
                minDist = d;
                nearest = proj;
            }
        }
    }
    return nearest;
}

/** 判断点是否在边界上或足够接近边界（用于绘制时吸附） */
function isPointOnOrNearBoundary(
    point: google.maps.LatLngLiteral,
    boundaryPaths: google.maps.LatLngLiteral[][],
    epsilonSq: number
): boolean {
    const nearest = nearestPointOnBoundary(point, boundaryPaths);
    const d = (point.lat - nearest.lat) ** 2 + (point.lng - nearest.lng) ** 2;
    return d <= epsilonSq;
}

function toFiniteNumber(value: unknown): number | null {
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : null;
}

function toLatLngLiteral(value: any): google.maps.LatLngLiteral | null {
    if (!value) return null;

    // google.maps.LatLng-like: { lat(): number, lng(): number }
    if (typeof value.lat === 'function' && typeof value.lng === 'function') {
        const lat = toFiniteNumber(value.lat());
        const lng = toFiniteNumber(value.lng());
        if (lat == null || lng == null) return null;
        return { lat, lng };
    }

    // Object form: { lat, lng } / { latitude, longitude }
    const objLat = toFiniteNumber(value.lat ?? value.latitude);
    const objLng = toFiniteNumber(value.lng ?? value.longitude);
    if (objLat != null && objLng != null) {
        return { lat: objLat, lng: objLng };
    }

    // Tuple form: [lng, lat] (GeoJSON) or [lat, lng]
    if (Array.isArray(value) && value.length >= 2) {
        const a = toFiniteNumber(value[0]);
        const b = toFiniteNumber(value[1]);
        if (a == null || b == null) return null;

        const asLngLat = Math.abs(a) <= 180 && Math.abs(b) <= 90; // [lng, lat]
        const asLatLng = Math.abs(a) <= 90 && Math.abs(b) <= 180; // [lat, lng]

        if (asLngLat && !asLatLng) return { lat: b, lng: a };
        if (asLatLng && !asLngLat) return { lat: a, lng: b };

        // Ambiguous: prefer GeoJSON [lng, lat]
        if (asLngLat) return { lat: b, lng: a };
        return null;
    }

    return null;
}

function sanitizeLatLngPath(path: any): google.maps.LatLngLiteral[] {
    if (!Array.isArray(path)) return [];
    return path.map(toLatLngLiteral).filter((p): p is google.maps.LatLngLiteral => p != null);
}

const defaultCenter = { lat: 39.8283, lng: -98.5795 };

/** 根据是否处于绘制模式返回地图选项（绘制时禁用拖拽、双击缩放，光标为十字） */
const getMapOptions = (drawMode: boolean, mapTypeId: 'roadmap' | 'satellite' = 'roadmap'): google.maps.MapOptions => ({
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    mapTypeId,
    draggable: !drawMode,
    draggableCursor: drawMode ? 'crosshair' : 'default',
    disableDoubleClickZoom: drawMode,
    clickableIcons: false, // 禁止点击地图上的景点和POI图标
});

/** 围栏状态配色：APPROVED 蓝、EFFECTIVE 绿、REJECTED/INACTIVE（两级审批不通过）红，CREATED 黄 */
const getGeofenceColors = (status: string): { fill: string; label: string } => {
    switch (status) {
        case 'APPROVED':
            return { fill: '#3B82F6', label: '#1D4ED8' };
        case 'EFFECTIVE':
            return { fill: '#22C55E', label: '#15803D' };
        case 'REJECTED':
        case 'INACTIVE':
            return { fill: '#EF4444', label: '#DC2626' };
        default:
            return { fill: '#FACC15', label: '#CA8A04' };
    }
};

interface ZipcodeMapPageProps {
    /** 内嵌抽屉模式：zipcode/from 由 props 传入，返回按钮改为关闭抽屉 */
    embedded?: boolean;
    zipcode?: string;
    from?: string;
    onClose?: () => void;
}

export const ZipcodeMapPage: React.FC<ZipcodeMapPageProps> = ({
    embedded = false,
    zipcode: zipcodeProp,
    from: fromProp,
    onClose,
}) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const zipcode = embedded ? (zipcodeProp || '') : (searchParams.get('zipcode') || '');
    // 来源标识：embedded 模式由 props 传入，独立路由页取 URL 参数
    const from = embedded ? fromProp : searchParams.get('from');

    console.log('🔍 ZipcodeMapPage 渲染, zipcode:', zipcode);

    // ---------- 状态：地图与边界 ----------
    const [boundaryPaths, setBoundaryPaths] = useState<google.maps.LatLngLiteral[][]>([]);
    const [geofenceData, setGeofenceData] = useState<Array<{
        path: google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][];
        status: string;
        id?: number | null;
        geofenceName: string;
    }>>([]);
    const [geofenceListData, setGeofenceListData] = useState<ZipcodeMapGeofenceType[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [mapReady, setMapReady] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    // 地图类型：矢量地图（roadmap，默认）/ 卫星地图（satellite）
    const [mapTypeId, setMapTypeId] = useState<'roadmap' | 'satellite'>('roadmap');
    // ---------- 状态：图例显隐开关（默认全显示） ----------
    const [showZipcodeBoundary, setShowZipcodeBoundary] = useState(true);
    const [showPendingGeofences, setShowPendingGeofences] = useState(true);
    const [showApprovedGeofences, setShowApprovedGeofences] = useState(true);
    const [showEffectiveGeofences, setShowEffectiveGeofences] = useState(true);
    const [showInactiveGeofences, setShowInactiveGeofences] = useState(true);
    // ---------- 状态：绘制围栏 ----------
    const [drawMode, setDrawMode] = useState(false);
    const [drawPolygonPaths, setDrawPolygonPaths] = useState<google.maps.LatLngLiteral[]>([]);
    const [drawnGeofencePolygons, setDrawnGeofencePolygons] = useState<google.maps.LatLngLiteral[][]>([]);
    const [mousePosition, setMousePosition] = useState<google.maps.LatLngLiteral | null>(null);
    const [drawVersion, setDrawVersion] = useState(0);
    const [isClearing, setIsClearing] = useState(false);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [currentDrawnPath, setCurrentDrawnPath] = useState<google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][] | null>(null);
    const [previewClippedPath, setPreviewClippedPath] = useState<google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][] | null>(null);
    // ---------- 状态：右侧列表与弹窗 ----------
    const [listDrawerVisible, setListDrawerVisible] = useState(true);
    const [auditModalVisible, setAuditModalVisible] = useState(false);
    const [currentAuditRecord, setCurrentAuditRecord] = useState<ZipcodeMapGeofenceType | null>(null);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [currentDeleteRecord, setCurrentDeleteRecord] = useState<ZipcodeMapGeofenceType | null>(null);
    const [geofenceInfoModalVisible, setGeofenceInfoModalVisible] = useState(false);
    const [selectedGeofence, setSelectedGeofence] = useState<ZipcodeMapGeofenceType | null>(null);
    const [helpModalVisible, setHelpModalVisible] = useState(false);
    const [tablePagination, setTablePagination] = useState({
        current: 1,
        pageSize: 10,
    });
    // 地图容器 ref，用于：
    // 1）控制浏览器全屏（requestFullscreen）
    // 2）作为 Antd Modal / Message 的挂载节点，确保全屏时浮层仍在地图上层
    const mapContainerRef = React.useRef<HTMLDivElement>(null);

    const [form] = Form.useForm();
    // Form 监听：描述类型变化时联动“其他描述”显示
    const descriptionValue = Form.useWatch('description', form);

    // 全局设置 Antd message 的挂载容器为地图容器：
    // - 避免浏览器全屏时，默认挂到 <body> 的提示被“全屏节点树”裁掉
    // - 所有 message.xxx 调用将自动出现在地图上层，而不需要在每次调用前手动 config
    useEffect(() => {
        message.config({
            getContainer: () => mapContainerRef.current || document.body,
        });
        // 卸载时恢复默认挂载容器，避免全局配置残留影响其它页面
        return () => {
            message.config({
                getContainer: () => document.body,
            });
        };
    }, []);

    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: GOOGLE_MAPS_API_KEY,
        preventGoogleFontsLoading: true,
    });

    console.log('🗺️ Google Maps 加载状态 - isLoaded:', isLoaded, 'loadError:', loadError);

    // ---------- 数据：步骤1 获取 zipcode 地图边界（紫色多边形） ----------
    const [fetchZipcodeMapBoundary, { loading: boundaryLoading }] = useLazyQuery<GetZipcodeMapBoundaryResponse>(
        GET_ZIPCODE_MAP_BOUNDARY,
        {
            fetchPolicy: 'network-only',
            onError: (error) => {
                console.error('❌ 步骤1失败 - 获取地图边界失败:', error);
                setError(error.message || 'Failed to load map boundary');
                setBoundaryPaths([]);
            },
            onCompleted: (data) => {
                console.log('✅ 步骤1成功 - 获取地图边界成功:', data);
                if (data?.getZipcodeMapBoundary) {
                    try {
                        const boundarys = data.getZipcodeMapBoundary;

                        const newBoundaryPaths: google.maps.LatLngLiteral[][] = Array.isArray(boundarys)
                            ? boundarys
                                .map((boundary) => {
                                    try {
                                        // 后端 boundary 字段可能是：
                                        // 1) 简单环: [{lat,lng}, ...]
                                        // 2) 带洞多边形: [[{lat,lng}, ...], [{lat,lng}, ...], ...]
                                        const coords = JSON.parse(boundary.boundary);
                                        const rawPath = Array.isArray(coords?.[0]) ? coords[0] : coords;
                                        const sanitized = sanitizeLatLngPath(rawPath);
                                        if (sanitized.length === 0) {
                                            console.warn('⚠️ zipcode 边界坐标无有效点，可能是坐标结构不匹配:', {
                                                zipcode,
                                                boundaryId: (boundary as any)?.id,
                                                sample: Array.isArray(coords) ? coords.slice(0, 2) : coords,
                                            });
                                            return null;
                                        }
                                        return sanitized;
                                    } catch (e) {
                                        console.error('❌ 解析单个 zipcode 边界失败，boundary:', boundary, '错误:', e);
                                        return null;
                                    }
                                })
                                .filter((path): path is google.maps.LatLngLiteral[] => Array.isArray(path) && path.length > 0)
                            : [];

                        if (!Array.isArray(boundarys)) {
                            console.error('❌ 未知的边界数据结构，期望为数组');
                        }

                        console.log('🗺️ 解析后的 zipcode boundaryPaths 数量:', newBoundaryPaths.length);
                        if (newBoundaryPaths.length > 0) {
                            const allPoints = newBoundaryPaths.flat();
                            const lats = allPoints.map(p => p.lat);
                            const lngs = allPoints.map(p => p.lng);
                            console.log('🗺️ zipcode boundaryPaths 经纬度范围:', {
                                minLat: Math.min(...lats),
                                maxLat: Math.max(...lats),
                                minLng: Math.min(...lngs),
                                maxLng: Math.max(...lngs),
                                samplePoints: allPoints.slice(0, 5),
                            });
                        }

                        setBoundaryPaths(newBoundaryPaths);
                        setError(null);
                        
                        // 步骤1成功后，执行步骤2
                        console.log('🚀 步骤2开始 - 获取电子围栏列表');
                        fetchZipcodeGeofence({ variables: { zipcode } });
                    } catch (err) {
                        console.error('❌ 解析边界数据失败:', err);
                        console.error('❌ 错误堆栈:', err);
                        setError('Failed to parse boundary data');
                        setBoundaryPaths([]);
                    }
                } else {
                    console.warn('⚠️ 没有找到该邮编的地图边界');
                    setBoundaryPaths([]);
                    setError('No boundary data found for this zipcode');
                }
            }
        }
    );

    // ---------- 数据：步骤2 获取电子围栏列表（用于地图渲染与右侧表格） ----------
    const [fetchZipcodeGeofence, { loading: geofenceLoading }] = useLazyQuery<GetZipcodeGeofenceListingResponse>(
        GET_ZIPCODE_GEOFENCE_LISTING,
        {
            fetchPolicy: 'network-only',
            onError: (error) => {
                console.error('❌ 步骤2失败 - 获取电子围栏失败:', error);
                // 不设置 error，因为步骤1已经成功了
            },
            onCompleted: (data) => {
                console.log('✅ 步骤2成功 - 获取电子围栏成功:', data);
                if (data?.getZipcodeGeofenceListing && data.getZipcodeGeofenceListing.length > 0) {
                    console.log('📦 获取到', data.getZipcodeGeofenceListing.length, '个电子围栏');
                    try {
                        const geofences: Array<{
                            path: google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][];
                            status: string;
                            id?: number | null;
                            geofenceName: string;
                        }> = [];
                        
                        for (const geofence of data.getZipcodeGeofenceListing) {
                            console.log('🔄 解析围栏:', {
                                id: geofence.id,
                                geofenceName: geofence.geofenceName,
                                status: geofence.status
                            });
                            // boundary 是坐标信息列表的 JSON 字符串
                            const coordinates = JSON.parse(geofence.boundary);
                            console.log('📍 围栏坐标:', coordinates);
                            
                            // 将坐标数组转换为 LatLngLiteral 数组
                            // 支持两种格式：
                            // 1. 简单多边形: [{lat, lng}, {lat, lng}, ...]
                            // 2. 带洞的多边形: [[{lat, lng}, ...], [{lat, lng}, ...], ...]
                            if (Array.isArray(coordinates) && coordinates.length > 0) {
                                let path: google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][];
                                
                                // 检查是否为带洞的多边形（第一个元素是数组）
                                if (Array.isArray(coordinates[0])) {
                                    // 带洞的多边形
                                    path = coordinates.map((ring: any[]) => 
                                        ring.map((coord: any) => ({
                                            lat: coord.lat,
                                            lng: coord.lng
                                        }))
                                    );
                                    console.log('🕳️ 检测到带洞的多边形，环数量:', path.length);
                                } else {
                                    // 简单多边形
                                    path = coordinates.map((coord: any) => ({
                                        lat: coord.lat,
                                        lng: coord.lng
                                    }));
                                }
                                
                                geofences.push({
                                    path,
                                    status: geofence.status,
                                    id: geofence.id,
                                    geofenceName: geofence.geofenceName
                                });
                            }
                        }
                        console.log('✅ 总共生成', geofences.length, '个围栏');
                        setGeofenceData(geofences);
                        // 保存完整的列表数据用于表格显示
                        console.log('📋 设置表格数据:', data.getZipcodeGeofenceListing);
                        setGeofenceListData(data.getZipcodeGeofenceListing);
                    } catch (err) {
                        console.error('❌ 解析围栏数据失败:', err);
                    }
                } else {
                    console.log('ℹ️ 该邮编没有电子围栏');
                    setGeofenceData([]);
                    setGeofenceListData([]);
                }
            }
        }
    );

    // ---------- Mutation：添加 / 审核 / 删除电子围栏 ----------
    const [addGeofence, { loading: addGeofenceLoading }] = useMutation<AddZipcodeGeofenceResponse>(
        ADD_ZIPCODE_GEOFENCE,
        {
            onCompleted: (data) => {
                console.log('✅ 添加围栏成功:', data);
                if (data.addZipcodeGeofence) {
                    message.success(t('wms.zipcodeMapGeofence.messages.saveSuccess'));
                    // 重新获取围栏列表
                    fetchZipcodeGeofence({ variables: { zipcode } });
                } else {
                    message.error(t('wms.zipcodeMapGeofence.messages.saveFailed'));
                }
            },
            onError: (error) => {
                console.error('❌ 添加围栏失败:', error);
                message.error(t('wms.zipcodeMapGeofence.messages.saveFailedWithReason', { reason: error.message }));
            }
        }
    );

    // Mutation: 审核电子围栏
    const [auditGeofence, { loading: auditGeofenceLoading }] = useMutation<AuditZipcodeGeofenceResponse>(
        AUDIT_ZIPCODE_GEOFENCE,
        {
            onCompleted: (data) => {
                console.log('✅ 审核围栏成功:', data);
                if (data.auditZipcodeGeofence) {
                    message.success(t('wms.zipcodeMapGeofence.messages.auditSuccess'));
                    setAuditModalVisible(false);
                    setCurrentAuditRecord(null);
                    // 重新获取围栏列表
                    fetchZipcodeGeofence({ variables: { zipcode } });
                } else {
                    message.error(t('wms.zipcodeMapGeofence.messages.auditFailed'));
                }
            },
            onError: (error) => {
                console.error('❌ 审核围栏失败:', error);
                message.error(t('wms.zipcodeMapGeofence.messages.auditFailedWithReason', { reason: error.message }));
            }
        }
    );

    // Mutation: 删除电子围栏
    const [deleteGeofence, { loading: deleteGeofenceLoading }] = useMutation<DeleteZipcodeGeofenceResponse>(
        DELETE_ZIPCODE_GEOFENCE,
        {
            onCompleted: (data) => {
                console.log('✅ 删除围栏成功:', data);
                if (data.deleteZipcodeGeofence) {
                    message.success(t('wms.zipcodeMapGeofence.messages.deleteSuccess'));
                    setDeleteModalVisible(false);
                    setCurrentDeleteRecord(null);
                    // 重新获取围栏列表
                    fetchZipcodeGeofence({ variables: { zipcode } });
                } else {
                    message.error(t('wms.zipcodeMapGeofence.messages.deleteFailed'));
                }
            },
            onError: (error) => {
                console.error('❌ 删除围栏失败:', error);
                message.error(t('wms.zipcodeMapGeofence.messages.deleteFailedWithReason', { reason: error.message }));
            }
        }
    );

    const loading = boundaryLoading || geofenceLoading || addGeofenceLoading || auditGeofenceLoading || deleteGeofenceLoading;
    console.log('📊 加载状态 - boundaryLoading:', boundaryLoading, 'geofenceLoading:', geofenceLoading, 'addGeofenceLoading:', addGeofenceLoading, 'deleteGeofenceLoading:', deleteGeofenceLoading);

    // ---------- 右侧表格列定义（ID 可点击定位到对应围栏，与围栏 Tab 共用列工厂） ----------
    // 用 wrapper 箭头延迟解析 handleXxx（其声明在本组件下方，build 时尚处于 TDZ）
    // 来源为黑名单围栏审核（终审）：仅「待终审」(APPROVED) 显示审核按钮，其他状态显示 "-"，隐藏删除
    const isGeofenceReview = from === 'geofence-review';

    // 是否有进行中/未提交的绘制内容（与「清除绘制」按钮显示条件一致），此时禁止切换 zipcode 围栏显隐
    const hasDrawingInProgress = drawMode || drawPolygonPaths.length > 0 || drawnGeofencePolygons.length > 0;

    // 渲染层可见围栏：受图例开关控制（REJECTED 不受控制常显）；
    // 绘制时的裁剪逻辑仍使用全量 geofenceData，隐藏不改变「不可与已有围栏重叠」的约束
    const visibleGeofenceData = useMemo(() => {
        return geofenceData.filter((geofence) => {
            if (geofence.status === 'CREATED' && !showPendingGeofences) return false;
            if (geofence.status === 'APPROVED' && !showApprovedGeofences) return false;
            if (geofence.status === 'EFFECTIVE' && !showEffectiveGeofences) return false;
            if (geofence.status === 'INACTIVE' && !showInactiveGeofences) return false;
            return true;
        });
    }, [geofenceData, showPendingGeofences, showApprovedGeofences, showEffectiveGeofences, showInactiveGeofences]);

    const columns = buildGeofenceColumns({
        t,
        onIdClick: (record) => handleIdClick(record),
        onAudit: (record) => handleAuditClick(record),
        onDelete: (record) => handleDeleteClick(record),
        ...(isGeofenceReview
            ? {
                auditableStatus: 'APPROVED',
                auditOnlyWhenAuditable: true,
                auditResourceCodes: [ResourceCode.menu.GEOFENCE_BLACKLIST_REVIEW],
            }
            : {}),
    });

    // ---------- Effect：zipcode 变化时拉取边界并触发步骤2 ----------
    useEffect(() => {
        console.log('🔄 useEffect 触发, zipcode:', zipcode);
        if (!zipcode) {
            console.warn('⚠️ zipcode 为空，不执行查询');
            setError('zipcode required');
            return;
        }
        console.log('🚀 步骤1开始 - 获取地图边界, zipcode:', zipcode);
        setError(null);
        setBoundaryPaths([]);
        setGeofenceData([]);
        
        // 只调用步骤1，步骤2会在步骤1成功后自动调用
        fetchZipcodeMapBoundary({ variables: { zipcode } });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zipcode]);

    // ---------- 业务方法：边界判断、裁剪、导航 ----------
    // 返回按钮：embedded 模式关闭抽屉；独立路由页根据来源（from 参数）返回对应入口，默认回 WMS 邮编列表页
    const handleBack = useCallback(() => {
        if (embedded) {
            onClose?.();
            return;
        }
        switch (from) {
            case 'geofence-review':
                navigate('/central/map-tool/geofence-blacklist-review');
                break;
            default:
                navigate('/wms/zipcode-map-geofence');
        }
    }, [navigate, from, embedded, onClose]);

    const isPointInBoundary = useCallback((point: google.maps.LatLngLiteral) => {
        return boundaryPaths.some((path) => isPointInPolygon(point, path));
    }, [boundaryPaths]);

    const isPointInsideOrOnBoundary = useCallback((point: google.maps.LatLngLiteral) => {
        if (boundaryPaths.length === 0) return true;
        if (isPointInBoundary(point)) return true;
        const epsilonSq = 1e-10;
        return isPointOnOrNearBoundary(point, boundaryPaths, epsilonSq);
    }, [boundaryPaths, isPointInBoundary]);

    /** 
     * 实时预览裁剪（不显示消息提示，用于绘制过程中的实时预览）
     */
    const clipDrawnPolygonPreview = useCallback((drawnPath: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][] | null => {
        try {
            const drawnCoords = [...drawnPath.map(p => [p.lng, p.lat]), [drawnPath[0].lng, drawnPath[0].lat]];
            let drawnPolygon: any = turf.polygon([drawnCoords]);

            // 与 zipcode 边界求交集
            if (boundaryPaths.length > 0) {
                const boundaryPolygons = boundaryPaths.map(path => {
                    const coords = [...path.map(p => [p.lng, p.lat]), [path[0].lng, path[0].lat]];
                    return turf.polygon([coords]);
                });
                let boundaryUnion: any = boundaryPolygons[0];
                for (let i = 1; i < boundaryPolygons.length; i++) {
                    const result = turf.union(turf.featureCollection([boundaryUnion, boundaryPolygons[i]]));
                    if (result) boundaryUnion = result;
                }
                const intersected = turf.intersect(turf.featureCollection([drawnPolygon, boundaryUnion]));
                if (!intersected) return null;
                drawnPolygon = intersected;
            }

            // 与已有围栏求差集
            if (geofenceData.length > 0) {
                let result: any = drawnPolygon;
                for (const geofence of geofenceData) {
                    if (geofence.path) {
                        // 处理简单多边形或带洞的多边形
                        let existingPolygon: any;
                        if (Array.isArray(geofence.path[0])) {
                            // 带洞的多边形：path 是数组的数组
                            const rings = (geofence.path as google.maps.LatLngLiteral[][]).map(ring => 
                                [...ring.map(p => [p.lng, p.lat]), [ring[0].lng, ring[0].lat]]
                            );
                            existingPolygon = turf.polygon(rings);
                        } else {
                            // 简单多边形：path 是简单数组
                            const coords = [...(geofence.path as google.maps.LatLngLiteral[]).map(p => [p.lng, p.lat]), [(geofence.path as google.maps.LatLngLiteral[])[0].lng, (geofence.path as google.maps.LatLngLiteral[])[0].lat]];
                            existingPolygon = turf.polygon([coords]);
                        }
                        
                        const diff = turf.difference(turf.featureCollection([result, existingPolygon]));
                        if (!diff) return null;
                        result = diff;
                    }
                }
                drawnPolygon = result;
            }

            // 转换回 Google Maps 格式（支持带洞的多边形）
            const geometry = drawnPolygon.geometry;
            if (geometry.type === 'Polygon') {
                // Polygon 的 coordinates 是一个数组：[外环, 洞1, 洞2, ...]
                const allRings = geometry.coordinates.map((ring: number[][]) => 
                    ring.slice(0, -1).map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
                );
                return allRings.length === 1 ? allRings[0] : allRings;
            } else if (geometry.type === 'MultiPolygon') {
                const polygons = geometry.coordinates.map((poly: number[][][]) => {
                    const allRings = poly.map((ring: number[][]) => 
                        ring.slice(0, -1).map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
                    );
                    return allRings.length === 1 ? allRings[0] : allRings;
                });
                let maxPolygon = polygons[0];
                let maxArea = 0;
                for (const poly of polygons) {
                    const polyCoords = Array.isArray(poly[0]) && typeof poly[0][0] === 'object' 
                        ? poly.map((ring: any) => [...ring.map((p: any) => [p.lng, p.lat]), [ring[0].lng, ring[0].lat]])
                        : [[...poly.map((p: any) => [p.lng, p.lat]), [(poly as any)[0].lng, (poly as any)[0].lat]]];
                    const turfPoly = turf.polygon(polyCoords);
                    const area = turf.area(turfPoly);
                    if (area > maxArea) {
                        maxArea = area;
                        maxPolygon = poly;
                    }
                }
                return maxPolygon;
            }
            return null;
        } catch (error) {
            return null;
        }
    }, [boundaryPaths, geofenceData]);

    /** 
     * 使用 Turf.js 将当前绘制多边形进行几何裁剪，返回一个“合法且可保存”的围栏：
     * 1）与 zipcode 边界求交（裁掉越界区域）
     * 2）与已有围栏求差（移除与已存在围栏重叠的部分）
     * 3）过滤掉面积过小的退化多边形
     */
    const clipDrawnPolygon = useCallback((drawnPath: google.maps.LatLngLiteral[]): google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][] | null => {
        try {
            // 1. 将绘制的路径转换为 Turf.js Polygon（需要闭合）
            const drawnCoords = [...drawnPath.map(p => [p.lng, p.lat]), [drawnPath[0].lng, drawnPath[0].lat]];
            let drawnPolygon: any = turf.polygon([drawnCoords]);

            // 输出原始绘制区域的面积
            const originalArea = turf.area(drawnPolygon);
            console.log('📐 原始绘制区域面积:', originalArea.toFixed(2), '平方米');

            // 2. 与 zipcode 边界求交集（裁剪超出部分）
            if (boundaryPaths.length > 0) {
                const boundaryPolygons = boundaryPaths.map(path => {
                    const coords = [...path.map(p => [p.lng, p.lat]), [path[0].lng, path[0].lat]];
                    return turf.polygon([coords]);
                });
                
                // 合并所有边界多边形
                let boundaryUnion: any = boundaryPolygons[0];
                for (let i = 1; i < boundaryPolygons.length; i++) {
                    const result = turf.union(turf.featureCollection([boundaryUnion, boundaryPolygons[i]]));
                    if (result) boundaryUnion = result;
                }
                
                // 与边界求交集
                const intersected = turf.intersect(turf.featureCollection([drawnPolygon, boundaryUnion]));
                if (!intersected) {
                    message.error(t('wms.zipcodeMapGeofence.messages.drawOutsideBoundary'));
                    return null;
                }
                drawnPolygon = intersected;
                
                // 输出与边界求交后的面积
                const afterBoundaryClipArea = turf.area(drawnPolygon);
                console.log('📐 与边界求交后面积:', afterBoundaryClipArea.toFixed(2), '平方米');
            }

            // 3. 与已有围栏求差集（移除与已存在围栏重叠的区域，包括完全包含的情况）
            if (geofenceData.length > 0) {
                let result: any = drawnPolygon;
                const containedFences: string[] = [];

                for (const geofence of geofenceData) {
                    if (geofence.path) {
                        // 处理简单多边形或带洞的多边形
                        let existingPolygon: any;
                        if (Array.isArray(geofence.path[0])) {
                            // 带洞的多边形：path 是数组的数组
                            const rings = (geofence.path as google.maps.LatLngLiteral[][]).map(ring => 
                                [...ring.map(p => [p.lng, p.lat]), [ring[0].lng, ring[0].lat]]
                            );
                            existingPolygon = turf.polygon(rings);
                        } else {
                            // 简单多边形：path 是简单数组
                            const coords = [...(geofence.path as google.maps.LatLngLiteral[]).map(p => [p.lng, p.lat]), [(geofence.path as google.maps.LatLngLiteral[])[0].lng, (geofence.path as google.maps.LatLngLiteral[])[0].lat]];
                            existingPolygon = turf.polygon([coords]);
                        }

                        // 检查新围栏是否完全包含已存在的围栏
                        const isContained = turf.booleanContains(result, existingPolygon);
                        if (isContained) {
                            containedFences.push(geofence.geofenceName);
                            console.log(`✂️ 新围栏完全包含已存在围栏: ${geofence.geofenceName}，将进行裁剪`);
                        }

                        // 求差集（无论是否完全包含，都需要裁剪掉重叠部分）
                        const diff = turf.difference(turf.featureCollection([result, existingPolygon]));
                        if (!diff) {
                            message.error(t('wms.zipcodeMapGeofence.messages.drawCoveredByExisting'));
                            return null;
                        }
                        result = diff;
                    }
                }

                // 如果裁剪掉了完全包含的围栏，给用户提示
                if (containedFences.length > 0) {
                    message.info(`已自动裁剪掉完全包含的围栏: ${containedFences.join(', ')}`);
                }

                drawnPolygon = result;
                
                // 输出与已有围栏求差后的面积
                const afterGeofenceClipArea = turf.area(drawnPolygon);
                console.log('📐 与已有围栏求差后面积:', afterGeofenceClipArea.toFixed(2), '平方米');
            }

            // 4. 检查面积，过滤掉退化的多边形（如裁剪后仅剩窄条或几乎为线段）
            const area = turf.area(drawnPolygon);
            const minArea = 20; // 最小面积阈值（平方米），可根据实际需求调整
            if (area < minArea) {
                message.error(t('wms.zipcodeMapGeofence.messages.areaTooSmall', { minArea }));
                console.log('❌ 裁剪后面积过小:', area.toFixed(2), '平方米');
                return null;
            }
            console.log('✅ 最终围栏面积:', area.toFixed(2), '平方米 (', (area / 1000000).toFixed(4), '平方公里)');

            // 5. 转换回 Google Maps 格式（支持带洞的多边形）
            const geometry = drawnPolygon.geometry;
            if (geometry.type === 'Polygon') {
                // Polygon 的 coordinates 是一个数组：[外环, 洞1, 洞2, ...]
                // Google Maps Polygon 的 paths 参数支持数组的数组来表示带洞的多边形
                const allRings = geometry.coordinates.map((ring: number[][]) => 
                    ring.slice(0, -1).map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
                );
                
                // 如果只有一个环（无洞），返回简单数组
                // 如果有多个环（带洞），返回数组的数组
                return allRings.length === 1 ? allRings[0] : allRings;
            } else if (geometry.type === 'MultiPolygon') {
                // 如果差集结果是 MultiPolygon，取面积最大的那个
                const polygons = geometry.coordinates.map((poly: number[][][]) => {
                    const allRings = poly.map((ring: number[][]) => 
                        ring.slice(0, -1).map((coord: number[]) => ({ lat: coord[1], lng: coord[0] }))
                    );
                    return allRings.length === 1 ? allRings[0] : allRings;
                });
                
                // 计算每个多边形的面积，返回面积最大的
                let maxPolygon = polygons[0];
                let maxArea = 0;
                for (const poly of polygons) {
                    const polyCoords = Array.isArray(poly[0]) && typeof poly[0][0] === 'object' 
                        ? poly.map((ring: any) => [...ring.map((p: any) => [p.lng, p.lat]), [ring[0].lng, ring[0].lat]])
                        : [[...poly.map((p: any) => [p.lng, p.lat]), [(poly as any)[0].lng, (poly as any)[0].lat]]];
                    const turfPoly = turf.polygon(polyCoords);
                    const area = turf.area(turfPoly);
                    if (area > maxArea) {
                        maxArea = area;
                        maxPolygon = poly;
                    }
                }
                return maxPolygon;
            }

            return null;
        } catch (error) {
            console.error('❌ 裁剪绘制区域失败:', error);
            message.error(t('wms.zipcodeMapGeofence.messages.clipFailed'));
            return null;
        }
    }, [boundaryPaths, geofenceData, t]);

    // ---------- 绘制相关：进入/完成/清除绘制、提交表单 ----------
    // 切换绘制模式按钮：
    // - 非绘制状态：进入绘制模式，清空上一次绘制的锚点与预览态
    // - 绘制状态：视为“完成绘制”，执行几何裁剪并打开围栏信息表单
    const handleDrawMode = useCallback(() => {
        if (drawMode) {
            // 点击"绘制完成"按钮
            if (drawPolygonPaths.length >= 3) {
                const completedPath = [...drawPolygonPaths];
                console.log('🎨 完成绘制围栏，坐标列表:', completedPath);
                console.log('🎨 坐标数量:', completedPath.length);
                
                // 使用 Turf.js 裁剪绘制区域
                // 将原始鼠标点路径裁剪到 zipcode 边界内，并减去已存在围栏
                const clippedPath = clipDrawnPolygon(completedPath);
                if (!clippedPath) {
                    console.log('❌ 裁剪失败，不保存');
                    return;
                }
                
                console.log('✂️ 裁剪后的路径:', clippedPath);
                console.log('✂️ 裁剪后坐标数量:', clippedPath.length);
                
                // 保存裁剪后的路径，后续表单提交时序列化到后端
                setCurrentDrawnPath(clippedPath);
                
                // 只清空鼠标 hover 预览，保留最终多边形的视觉结果
                setMousePosition(null);
                
                // 打开围栏信息表单弹窗，由用户补充围栏元数据
                setDrawerVisible(true);
                
                // 重置表单
                form.resetFields();
            } else {
                message.warning(t('wms.zipcodeMapGeofence.messages.minPointsToComplete'));
            }
        } else {
            // 点击"绘制围栏"按钮，开始绘制
            setDrawMode(true);
            setDrawPolygonPaths([]);
            setMousePosition(null);
            setPreviewClippedPath(null);
        }
    }, [drawMode, drawPolygonPaths, form, clipDrawnPolygon]);

    // 清空绘制：重置所有与本次绘制相关的状态，并记录当前地图视图，避免刷新后视角跳变
    const handleClearDrawing = useCallback(() => {
        console.log('🧹 开始清除绘制');

        // 在触发重新加载前记录当前地图视图（中心点和缩放级别），
        // 供后续地图重新挂载后通过 lastMapViewRef 恢复
        if (mapRef.current) {
            const center = mapRef.current.getCenter();
            const zoom = mapRef.current.getZoom();
            if (center != null && zoom != null) {
                lastMapViewRef.current = {
                    center: {
                        lat: center.lat(),
                        lng: center.lng(),
                    },
                    zoom,
                };
                restoreViewAfterRemountRef.current = true;
                console.log('🧭 已记录清除前地图视图:', lastMapViewRef.current);
            }
        }

        // isClearing + drawVersion 共同用于强制重渲染绘制层，避免“残影”问题
        setIsClearing(true);
        flushSync(() => {
            console.log('🧹 flushSync: 清空状态');
            setMousePosition(null);
            setDrawPolygonPaths([]);
            setDrawnGeofencePolygons([]);
            setDrawMode(false);
            setCurrentDrawnPath(null);
            setPreviewClippedPath(null);
        });
        console.log('🧹 设置延时更新版本号');
        setTimeout(() => {
            console.log('🧹 延时回调: 更新版本号并结束清除状态');
            flushSync(() => {
                setDrawVersion((v) => v + 1);
                setIsClearing(false);
            });
            // 视图恢复逻辑放到地图就绪后的 useEffect 中统一处理
        }, 0);
    }, [boundaryPaths]);

    const handleDrawerClose = useCallback(() => {
        console.log('❌ 取消绘制围栏，调用清除绘制事件');
        setDrawerVisible(false);
        setCurrentDrawnPath(null);
        // 调用清除绘制按钮事件，刷新地图页面
        handleClearDrawing();
    }, [handleClearDrawing]);

    const handleDrawerSubmit = useCallback(() => {
        form.validateFields().then((values) => {
            console.log('📝 提交围栏信息:', values);
            console.log('📍 围栏坐标:', currentDrawnPath);
            
            if (!currentDrawnPath) {
                message.error(t('wms.zipcodeMapGeofence.messages.noDrawnPolygon'));
                return;
            }

            // 获取当前用户信息
            const userInfo = getUserInfo();
            if (!userInfo || !userInfo.account || !userInfo.account.id) {
                message.error(t('wms.zipcodeMapGeofence.messages.missingUserInfo'));
                return;
            }

            // 构造围栏数据
            const geofenceData = {
                zipcode: zipcode,
                geometryType: 'Polygon',
                boundary: JSON.stringify(currentDrawnPath),
                notes: null,
                status: 'CREATED',
                descriptionType: values.description,
                descriptionOther: values.otherDescription || null,
                creatorId: Number(userInfo.account.id),
                geofenceName: values.name,
                geofenceType: 'BLACKLIST'
            };

            console.log('🚀 调用 API 保存围栏:', geofenceData);

            // 调用 mutation
            addGeofence({
                variables: {
                    geofence: geofenceData
                }
            }).then(() => {
                console.log('✅ 提交成功，关闭抽屉并重置状态');
                
                // 关闭抽屉
                setDrawerVisible(false);
                setCurrentDrawnPath(null);
                
                // 调用清除绘制事件，完整重置状态
                handleClearDrawing();
            }).catch((error) => {
                console.error('❌ 保存围栏失败:', error);
                // 错误已在 mutation 的 onError 中处理
            });
        }).catch((errorInfo) => {
            console.error('表单验证失败:', errorInfo);
        });
    }, [form, currentDrawnPath, zipcode, addGeofence, handleClearDrawing]);

    // ---------- 审核、删除、围栏信息、点击 ID 定位 ----------
    const handleAuditClick = useCallback((record: ZipcodeMapGeofenceType) => {
        console.log('🔍 打开审核弹窗:', record);
        setCurrentAuditRecord(record);
        setAuditModalVisible(true);
    }, []);

    const handleAuditApprove = useCallback(() => {
        console.log('✅ 审核通过:', currentAuditRecord);

        if (!currentAuditRecord || !currentAuditRecord.id) {
            message.error(t('wms.zipcodeMapGeofence.messages.missingGeofenceInfo'));
            return;
        }

        auditGeofence({
            variables: {
                id: currentAuditRecord.id,
                result: true,
                level: isGeofenceReview ? 2 : 1
            }
        });
    }, [currentAuditRecord, auditGeofence, isGeofenceReview]);

    const handleAuditReject = useCallback(() => {
        console.log('❌ 审核拒绝:', currentAuditRecord);

        if (!currentAuditRecord || !currentAuditRecord.id) {
            message.error(t('wms.zipcodeMapGeofence.messages.missingGeofenceInfo'));
            return;
        }

        auditGeofence({
            variables: {
                id: currentAuditRecord.id,
                result: false,
                level: isGeofenceReview ? 2 : 1
            }
        });
    }, [currentAuditRecord, auditGeofence, isGeofenceReview]);

    const handleAuditCancel = useCallback(() => {
        setAuditModalVisible(false);
        setCurrentAuditRecord(null);
    }, []);

    const handleDeleteClick = useCallback((record: ZipcodeMapGeofenceType) => {
        console.log('🗑️ 打开删除确认弹窗:', record);
        setCurrentDeleteRecord(record);
        setDeleteModalVisible(true);
    }, []);

    const handleDeleteConfirm = useCallback(() => {
        console.log('✅ 确认删除围栏:', currentDeleteRecord);

        if (!currentDeleteRecord || !currentDeleteRecord.id) {
            message.error(t('wms.zipcodeMapGeofence.messages.missingGeofenceInfo'));
            return;
        }

        deleteGeofence({
            variables: {
                id: currentDeleteRecord.id
            }
        });
    }, [currentDeleteRecord, deleteGeofence]);

    const handleDeleteCancel = useCallback(() => {
        console.log('❌ 取消删除');
        setDeleteModalVisible(false);
        setCurrentDeleteRecord(null);
    }, []);

    const handleGeofenceClick = useCallback((geofence: ZipcodeMapGeofenceType) => {
        console.log('🗺️ 点击围栏:', geofence);
        setSelectedGeofence(geofence);
        setGeofenceInfoModalVisible(true);
    }, []);

    const handleGeofenceInfoModalClose = useCallback(() => {
        setGeofenceInfoModalVisible(false);
        setSelectedGeofence(null);
    }, []);

    const handleIdClick = useCallback((record: ZipcodeMapGeofenceType) => {
        console.log('🎯 点击ID，定位到围栏:', record);
        
        // 从 geofenceData 中找到对应的围栏路径
        const geofence = geofenceData.find(g => g.id === record.id);
        if (!geofence || !geofence.path || geofence.path.length === 0) {
            console.warn('⚠️ 未找到围栏路径数据');
            return;
        }

        if (!mapRef.current) {
            console.warn('⚠️ 地图未初始化');
            return;
        }

        // 计算围栏的边界
        const bounds = new google.maps.LatLngBounds();
        if (Array.isArray(geofence.path[0])) {
            // 带洞的多边形：只使用外环计算边界
            (geofence.path as google.maps.LatLngLiteral[][])[0].forEach(point => {
                bounds.extend(point);
            });
        } else {
            // 简单多边形
            (geofence.path as google.maps.LatLngLiteral[]).forEach(point => {
                bounds.extend(point);
            });
        }

        // 地图自动定位到围栏区域并放大
        mapRef.current.fitBounds(bounds, 50); // 50px padding
        
        console.log('✅ 地图已定位到围栏区域');
    }, [geofenceData]);

    // ---------- 地图交互：左键加点、右键完成、鼠标移动预览 ----------
    // 地图左键点击：在绘制模式下增加一个多边形顶点，
    // 若点落在边界外，则将其投影到最近的边界上并给出提示
    const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!drawMode || !e.latLng) return;
        const rawPoint = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        let finalPoint = rawPoint;
        if (boundaryPaths.length > 0 && !isPointInsideOrOnBoundary(rawPoint)) {
            finalPoint = nearestPointOnBoundary(rawPoint, boundaryPaths);
            message.info(t('wms.zipcodeMapGeofence.pointOutsideBoundary'));
        }
        setDrawPolygonPaths((prev) => {
            const newPaths = [...prev, finalPoint];
            // 如果有至少3个点，实时计算裁剪后的预览
            if (newPaths.length >= 3) {
                const clipped = clipDrawnPolygonPreview(newPaths);
                setPreviewClippedPath(clipped);
            } else {
                setPreviewClippedPath(null);
            }
            return newPaths;
        });
    }, [drawMode, isPointInsideOrOnBoundary, boundaryPaths, t, clipDrawnPolygonPreview]);

    // 将当前锚点列表视为一个完整多边形并执行几何裁剪，成功后打开表单。
    // 该函数既被“完成绘制”按钮调用，也被右键快捷完成调用。
    const handleCompleteDrawing = useCallback(() => {
        if (drawPolygonPaths.length >= 3) {
            const completedPath = [...drawPolygonPaths];
            console.log('🎨 完成绘制围栏，坐标列表:', completedPath);
            console.log('🎨 坐标数量:', completedPath.length);
            
            // 使用 Turf.js 裁剪绘制区域
            const clippedPath = clipDrawnPolygon(completedPath);
            if (!clippedPath) {
                console.log('❌ 裁剪失败，不保存');
                return;
            }
            
            console.log('✂️ 裁剪后的路径:', clippedPath);
            console.log('✂️ 裁剪后坐标数量:', clippedPath.length);
            
            // 保存裁剪后的路径
            setCurrentDrawnPath(clippedPath);
            
            // 只清空鼠标位置，保留绘制的路径显示
            setMousePosition(null);
            
            // 打开抽屉
            setDrawerVisible(true);
            
            // 重置表单
            form.resetFields();
        }
    }, [drawPolygonPaths, form, clipDrawnPolygon]);

    // 地图右键：在绘制模式下作为“完成绘制”的快捷键，
    // 若锚点不足 3 个则只给出提示，不进入裁剪/表单流程
    const handleRightClick = useCallback((e: google.maps.MapMouseEvent) => {
        if (!drawMode) return;
        e.stop?.();
        if (drawPolygonPaths.length >= 3) {
            handleCompleteDrawing();
        } else {
            message.warning(t('wms.zipcodeMapGeofence.messages.minPointsToComplete'));
        }
    }, [drawMode, drawPolygonPaths.length, handleCompleteDrawing]);

    const handleMouseMove = useMemo(
        () => throttle((e: google.maps.MapMouseEvent) => {
            if (drawMode && e.latLng && drawPolygonPaths.length > 0) {
                setMousePosition({ lat: e.latLng.lat(), lng: e.latLng.lng() });
            }
        }, 100),
        [drawMode, drawPolygonPaths.length]
    );

    // 切换浏览器原生全屏，仅针对地图容器节点：
    // - 进入全屏：地图占满屏幕，右侧列表仍悬浮在上层
    // - 退出全屏：恢复正常布局
    const toggleFullscreen = useCallback(() => {
        const el = mapContainerRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
            el.requestFullscreen?.().then(() => setIsFullscreen(true));
        } else {
            document.exitFullscreen?.().then(() => setIsFullscreen(false));
        }
    }, []);

    // 监听全屏变化并触发地图 resize，保证全屏切换后地图正确重绘
    useEffect(() => {
        const handler = () => {
            const fs = !!document.fullscreenElement;
            setIsFullscreen(fs);
            if (mapRef.current) {
                setTimeout(() => google.maps.event.trigger(mapRef.current!, 'resize'), 100);
            }
        };
        document.addEventListener('fullscreenchange', handler);
        return () => document.removeEventListener('fullscreenchange', handler);
    }, []);

    const mapRef = React.useRef<google.maps.Map | null>(null);
    const lastMapViewRef = React.useRef<{
        center: google.maps.LatLngLiteral;
        zoom: number;
    } | null>(null);
    const restoreViewAfterRemountRef = React.useRef(false);
    const onMapLoad = useCallback((map: google.maps.Map) => {
        // 地图加载完成：保存 ref，等 tilesloaded 后标记 mapReady
        mapRef.current = map;
        setMapReady(false);
        google.maps.event.addListenerOnce(map, 'tilesloaded', () => {
            setMapReady(true);
        });
    }, []);

    const onMapUnmount = useCallback(() => {
        mapRef.current = null;
        setMapReady(false);
    }, []);

    // 切换地图类型：矢量地图 / 卫星地图（同时更新受控状态，确保地图重挂载后保持选中类型）
    const handleMapTypeChange = useCallback((typeId: 'roadmap' | 'satellite') => {
        setMapTypeId(typeId);
        mapRef.current?.setMapTypeId(typeId);
    }, []);

    // 地图就绪后：
    // 1）如果有记录的视图（清除前），优先恢复到该视图
    // 2）否则在有边界数据时自动 fitBounds 到 zipcode 边界
    useEffect(() => {
        console.log('🗺️ fitBounds effect 触发:', {
            hasMap: !!mapRef.current,
            mapReady,
            boundaryPathsLength: boundaryPaths.length,
        });
        if (!mapRef.current || !mapReady) return;

        // 优先恢复清除前的地图视图
        if (restoreViewAfterRemountRef.current && lastMapViewRef.current) {
            const savedView = lastMapViewRef.current;
            mapRef.current.setCenter(savedView.center);
            mapRef.current.setZoom(savedView.zoom);
            restoreViewAfterRemountRef.current = false;
            console.log('🧭 地图就绪后已恢复到清除前视图:', savedView);
            return;
        }

        // 没有记录视图时，按照 zipcode 边界自适应
        if (boundaryPaths.length === 0) return;
        const bounds = new google.maps.LatLngBounds();
        boundaryPaths.forEach((path) => {
            path.forEach((p) => bounds.extend(p));
        });
        console.log('🗺️ 计算得到的边界 bounds:', bounds.toJSON());
        mapRef.current.fitBounds(bounds, 40);
    }, [mapReady, boundaryPaths]);

    // ---------- 地图图层 useMemo：边界多边形（紫色）、围栏多边形、标签、绘制预览等 ----------
    const boundaryPolygons = useMemo(() => {
        console.log('🟣 boundaryPolygons useMemo 触发:', {
            isLoaded,
            mapReady,
            boundaryPathsLength: boundaryPaths.length,
            boundaryPaths: boundaryPaths
        });
        
        if (!isLoaded) {
            console.log('🟣 Google Maps 未加载，不渲染');
            return null;
        }
        if (!mapReady) {
            console.log('🟣 地图未就绪，不渲染');
            return null;
        }
        if (!showZipcodeBoundary) {
            console.log('🟣 图例已隐藏 zipcode 围栏，不渲染');
            return null;
        }
        if (boundaryPaths.length === 0) {
            console.log('🟣 边界路径为空，不渲染');
            return null;
        }

        console.log('🟣 开始渲染边界多边形，数量:', boundaryPaths.length);
        const polygons = boundaryPaths.map((path, idx) => {
            console.log(`🟣 渲染第 ${idx + 1} 个多边形，点数:`, path.length);
            return (
                <Polygon
                    key={`boundary-${idx}`}
                    paths={path}
                    options={{
                        fillColor: '#8B5CF6',
                        fillOpacity: 0.35,
                        strokeColor: '#8B5CF6',
                        strokeOpacity: 1,
                        strokeWeight: 2,
                        clickable: false,
                    }}
                />
            );
        });
        console.log('🟣 边界多边形渲染完成');
        return polygons;
    }, [isLoaded, mapReady, boundaryPaths, showZipcodeBoundary]);

    /** 计算多边形中心点（用于标签位置，优先用网格找最大内接圆圆心，否则质心或 pointOnFeature） */
    const calculatePolygonCenter = useCallback((path: google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][]): google.maps.LatLngLiteral => {
        try {
            // 将路径转换为 Turf.js 多边形
            let polygon: any;
            if (Array.isArray(path[0])) {
                // 带洞的多边形
                const rings = (path as google.maps.LatLngLiteral[][]).map(ring => 
                    [...ring.map(p => [p.lng, p.lat]), [ring[0].lng, ring[0].lat]]
                );
                polygon = turf.polygon(rings);
            } else {
                // 简单多边形
                const coords = [...(path as google.maps.LatLngLiteral[]).map(p => [p.lng, p.lat]), [(path as google.maps.LatLngLiteral[])[0].lng, (path as google.maps.LatLngLiteral[])[0].lat]];
                polygon = turf.polygon([coords]);
            }
            
            // 方法1：使用 polylabel 算法找到最大内接圆的中心（视觉上最佳位置）
            // 这需要采样多个点来找到最佳位置
            const bbox = turf.bbox(polygon);
            const [minLng, minLat, maxLng, maxLat] = bbox;
            
            // 在边界框内生成网格点，找到距离边界最远的内部点
            const gridSize = 20; // 网格密度
            let bestPoint = null;
            let maxDistance = 0;
            
            for (let i = 0; i <= gridSize; i++) {
                for (let j = 0; j <= gridSize; j++) {
                    const lng = minLng + (maxLng - minLng) * (i / gridSize);
                    const lat = minLat + (maxLat - minLat) * (j / gridSize);
                    const testPoint = turf.point([lng, lat]);
                    
                    if (turf.booleanPointInPolygon(testPoint, polygon)) {
                        // 计算到多边形边界的最小距离
                        const polygonLine: any = turf.polygonToLine(polygon);
                        let distance = 0;
                        
                        // 处理 MultiLineString（带洞的多边形）和 LineString
                        if (polygonLine.geometry.type === 'MultiLineString') {
                            // 对于 MultiLineString，计算到每条线的距离，取最小值
                            const distances = polygonLine.geometry.coordinates.map((lineCoords: any) => {
                                const line = turf.lineString(lineCoords);
                                return turf.pointToLineDistance(testPoint, line, { units: 'meters' });
                            });
                            distance = Math.min(...distances);
                        } else {
                            // 对于 LineString，直接计算距离
                            distance = turf.pointToLineDistance(testPoint, polygonLine, { units: 'meters' });
                        }
                        
                        if (distance > maxDistance) {
                            maxDistance = distance;
                            bestPoint = { lat, lng };
                        }
                    }
                }
            }
            
            if (bestPoint) {
                return bestPoint;
            }
            
            // 方法2：降级到质心
            const centroid = turf.centroid(polygon);
            const centroidPoint = turf.point(centroid.geometry.coordinates);
            
            if (turf.booleanPointInPolygon(centroidPoint, polygon)) {
                const [lng, lat] = centroid.geometry.coordinates;
                return { lat, lng };
            }
            
            // 方法3：使用 pointOnFeature（保证在多边形表面）
            const pointOnSurface = turf.pointOnFeature(polygon);
            const [lng, lat] = pointOnSurface.geometry.coordinates;
            return { lat, lng };
        } catch (error) {
            console.error('❌ 计算多边形中心点失败，使用算术平均值:', error);
            // 降级方案：使用外环的算术平均值
            const outerRing = Array.isArray(path[0]) ? (path as google.maps.LatLngLiteral[][])[0] : (path as google.maps.LatLngLiteral[]);
            let latSum = 0;
            let lngSum = 0;
            for (const point of outerRing) {
                latSum += point.lat;
                lngSum += point.lng;
            }
            return {
                lat: latSum / outerRing.length,
                lng: lngSum / outerRing.length
            };
        }
    }, []);

    /** 已保存的电子围栏多边形：按状态配色（getGeofenceColors），受图例开关过滤，点击打开详情弹窗 */
    const geofencePolygons = useMemo(() => {
        if (!isLoaded || !mapReady || visibleGeofenceData.length === 0) return null;
        console.log('🟡 渲染电子围栏，数量:', visibleGeofenceData.length);
        return visibleGeofenceData.map((geofence, idx) => {
            const { fill: fillColor } = getGeofenceColors(geofence.status);
            const strokeColor = fillColor;

            console.log(`🔶 渲染围栏 ${idx + 1}: ${geofence.geofenceName}, status: ${geofence.status}, 颜色: ${fillColor}`);

            // 从 geofenceListData 中找到完整的围栏信息
            const fullGeofenceData = geofenceListData.find(g => g.id === geofence.id);
            
            return (
                <Polygon
                    key={`geofence-${geofence.id || idx}`}
                    paths={geofence.path}
                    options={{
                        fillColor: fillColor,
                        fillOpacity: 0.35,
                        strokeColor: strokeColor,
                        strokeOpacity: 1,
                        strokeWeight: 2,
                        clickable: !drawMode,
                    }}
                    onClick={() => !drawMode && fullGeofenceData && handleGeofenceClick(fullGeofenceData)}
                />
            );
        });
    }, [isLoaded, mapReady, visibleGeofenceData, geofenceListData, handleGeofenceClick, drawMode]);

    /** 围栏名称标签（Marker 置于多边形中心），随图例开关与多边形同步显隐 */
    const geofenceLabels = useMemo(() => {
        if (!isLoaded || !mapReady || visibleGeofenceData.length === 0) return null;
        console.log('🏷️ 渲染围栏标签，数量:', visibleGeofenceData.length);
        return visibleGeofenceData.map((geofence, idx) => {
            const center = calculatePolygonCenter(geofence.path);
            const { label: labelColor } = getGeofenceColors(geofence.status);

            console.log(`🏷️ 标签 ${idx + 1}: 名称="${geofence.geofenceName}", 状态=${geofence.status}, 中心点:`, center, '路径点数:', geofence.path.length);
            
            return (
                <Marker
                    key={`geofence-label-${geofence.id || idx}`}
                    position={center}
                    icon={{
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                            `<svg xmlns="http://www.w3.org/2000/svg" width="1" height="1">
                                <rect width="1" height="1" fill="transparent"/>
                            </svg>`
                        ),
                        scaledSize: { width: 1, height: 1 } as google.maps.Size,
                        anchor: { x: 0, y: 0 } as google.maps.Point,
                    }}
                    label={{
                        text: geofence.geofenceName,
                        color: labelColor,
                        fontSize: '14px',
                        fontWeight: 'bold',
                        className: 'geofence-label'
                    }}
                    clickable={false}
                />
            );
        });
    }, [isLoaded, mapReady, visibleGeofenceData, calculatePolygonCenter]);

    const createDrawPointIcon = useCallback((index: number): google.maps.Icon => ({
        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="10" fill="#FACC15" stroke="#FFFFFF" stroke-width="2"/>
                <text x="12" y="16" font-size="12" fill="#000" text-anchor="middle" font-weight="bold">${index + 1}</text>
            </svg>`
        ),
        scaledSize: { width: 24, height: 24 } as google.maps.Size,
        anchor: { x: 12, y: 12 } as google.maps.Point,
    }), []);
    // 绘制电子围栏的坐标小圆点并显示数字
    const geofenceVertexMarkers = useMemo(() => {
        if (!isLoaded || !mapReady || geofenceData.length === 0) return null;
        return geofenceData.flatMap((geofence, gIdx) => {
            const { fill: fillColor } = getGeofenceColors(geofence.status);

            // 处理简单多边形或带洞的多边形
            const outerRing = Array.isArray(geofence.path[0]) 
                ? (geofence.path as google.maps.LatLngLiteral[][])[0]
                : (geofence.path as google.maps.LatLngLiteral[]);
            
            return outerRing.map((point, pIdx) => (
                <Marker
                    key={`geofence-vertex-${geofence.id || gIdx}-${pIdx}`}
                    position={point}
                    icon={{
                        url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
                            `<svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22">
                                <circle cx="11" cy="11" r="9" fill="${fillColor}" stroke="#FFFFFF" stroke-width="2"/>
                                <text x="11" y="14" font-size="10" fill="#000" text-anchor="middle" font-weight="bold">${pIdx + 1}</text>
                            </svg>`
                        ),
                        scaledSize: { width: 22, height: 22 } as google.maps.Size,
                        anchor: { x: 11, y: 11 } as google.maps.Point,
                    }}
                    clickable={false}
                />
            ));
        });
    }, [isLoaded, mapReady, geofenceData]);

    /** 绘制模式下的已点击锚点（蓝色圆点） */
    const drawPointMarkers = useMemo(() => {
        console.log('🔵 drawPointMarkers useMemo:', { isLoaded, drawMode, isClearing, pathsLength: drawPolygonPaths.length, drawVersion });
        if (!isLoaded || !drawMode || isClearing) {
            console.log('🔵 drawPointMarkers: 返回 null');
            return null;
        }
        console.log('🔵 drawPointMarkers: 渲染', drawPolygonPaths.length, '个点');
        return drawPolygonPaths.map((point, index) => (
            <Marker
                key={`draw-point-${drawVersion}-${index}`}
                position={point}
                icon={createDrawPointIcon(index)}
                clickable={false}
            />
        ));
    }, [isLoaded, drawMode, drawPolygonPaths, createDrawPointIcon, drawVersion, isClearing]);

    /** 绘制模式下已有点之间的连线（Polyline） */
    const drawConnectionLine = useMemo(() => {
        console.log('🟡 drawConnectionLine useMemo:', { isLoaded, drawMode, isClearing, pathsLength: drawPolygonPaths.length, drawVersion });
        if (!isLoaded || !drawMode || drawPolygonPaths.length <= 1 || isClearing) {
            console.log('🟡 drawConnectionLine: 返回 null');
            return null;
        }
        console.log('🟡 drawConnectionLine: 渲染连接线，路径点数:', drawPolygonPaths.length);
        return (
            <Polyline
                key={`draw-connection-${drawVersion}`}
                path={drawPolygonPaths}
                options={{
                    strokeColor: '#FACC15',
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    clickable: false,
                }}
            />
        );
    }, [isLoaded, drawMode, drawPolygonPaths, drawVersion, isClearing]);

    /** 绘制模式下鼠标位置到最后一个点/第一个点的预览虚线 */
    const drawPreviewLines = useMemo(() => {
        console.log('🟠 drawPreviewLines useMemo:', { isLoaded, drawMode, isClearing, hasMousePos: !!mousePosition, pathsLength: drawPolygonPaths.length, drawVersion });
        if (!isLoaded || !drawMode || !mousePosition || drawPolygonPaths.length === 0 || isClearing) {
            console.log('🟠 drawPreviewLines: 返回 null');
            return null;
        }
        console.log('🟠 drawPreviewLines: 渲染预览线');
        const lines = [];
        lines.push(
            <Polyline
                key={`draw-preview-last-${drawVersion}`}
                path={[drawPolygonPaths[drawPolygonPaths.length - 1], mousePosition]}
                options={{
                    strokeColor: '#FACC15',
                    strokeOpacity: 0.6,
                    strokeWeight: 2,
                    clickable: false,
                }}
            />
        );
        if (drawPolygonPaths.length >= 2) {
            console.log('🟠 drawPreviewLines: 添加第一个点的预览线');
            lines.push(
                <Polyline
                    key={`draw-preview-first-${drawVersion}`}
                    path={[mousePosition, drawPolygonPaths[0]]}
                    options={{
                        strokeColor: '#FACC15',
                        strokeOpacity: 0.6,
                        strokeWeight: 2,
                        clickable: false,
                    }}
                />
            );
        }
        return lines;
    }, [isLoaded, drawMode, mousePosition, drawPolygonPaths, drawVersion, isClearing]);

    /** 绘制中未完成多边形的半透明填充 */
    const drawPolygonFill = useMemo(() => {
        if (!isLoaded || !drawMode || drawPolygonPaths.length < 3 || isClearing) return null;
        
        // 优先使用裁剪后的预览路径，如果没有则使用原始路径
        const pathsToRender = previewClippedPath || drawPolygonPaths;
        
        return (
            <Polygon
                key={`draw-fill-${drawVersion}`}
                paths={pathsToRender}
                options={{
                    fillColor: '#FACC15',
                    fillOpacity: 0.35,
                    strokeColor: 'transparent',
                    strokeOpacity: 0,
                    strokeWeight: 0,
                    clickable: false,
                }}
            />
        );
    }, [isLoaded, drawMode, drawPolygonPaths, drawVersion, isClearing, previewClippedPath]);

    /** 本次会话中已绘制但尚未提交的围栏多边形（提交前临时展示） */
    const drawnGeofencePolygonsRender = useMemo(() => {
        if (!isLoaded || drawnGeofencePolygons.length === 0 || isClearing) return null;
        return drawnGeofencePolygons.map((path, idx) => (
            <Polygon
                key={`drawn-geofence-${drawVersion}-${idx}`}
                paths={path}
                options={{
                    fillColor: '#FACC15',
                    fillOpacity: 0.35,
                    strokeColor: '#FACC15',
                    strokeOpacity: 1,
                    strokeWeight: 2,
                    clickable: false,
                }}
            />
        ));
    }, [isLoaded, drawnGeofencePolygons, drawVersion, isClearing]);

    // ---------- 渲染：加载失败时返回错误页，否则主布局（顶栏 + 地图 + 弹窗 + 右侧列表） ----------
    if (loadError) {
        return (
            <div className="p-6">
                <div className="text-red-500">Google Maps failed to load</div>
                <Button onClick={handleBack} className="mt-4">
                    {t('wms.zipcodeMapGeofence.back')}
                </Button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col ${embedded ? 'h-full' : 'h-screen'}`}>
            <div className="flex items-center gap-4 px-6 py-4 bg-white border-b border-gray-200">
                <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
                    {t('wms.zipcodeMapGeofence.back')}
                </Button>
                <span className="font-semibold text-lg">{t('wms.zipcodeMapGeofence.title')} - {zipcode || '-'}</span>
            </div>
            <div
                ref={mapContainerRef}
                className="flex-1 min-h-[400px] relative rounded-lg overflow-hidden"
            >
                {isLoaded && !loading && !error && (
                    <div className="absolute top-4 right-4 z-20 flex gap-2">
                        <Button
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={toggleFullscreen}
                            style={{ color: '#8B5CF6' }}
                        >
                            {isFullscreen ? t('wms.zipcodeMapGeofence.exitFullscreen') : t('wms.zipcodeMapGeofence.fullscreen')}
                        </Button>
                        <Button
                            type={drawMode ? 'primary' : 'default'}
                            icon={<EditOutlined />}
                            onClick={handleDrawMode}
                            disabled={!showZipcodeBoundary}
                            style={!drawMode && showZipcodeBoundary ? { color: '#8B5CF6' } : undefined}
                        >
                            {drawMode ? t('wms.zipcodeMapGeofence.drawComplete') : t('wms.zipcodeMapGeofence.drawGeofence')}
                        </Button>
                        {(drawMode || drawPolygonPaths.length > 0 || drawnGeofencePolygons.length > 0) && (
                            <Button
                                icon={<ClearOutlined />}
                                onClick={handleClearDrawing}
                                danger
                            >
                                {t('wms.zipcodeMapGeofence.clearDrawing')}
                            </Button>
                        )}
                            <Button
                                icon={<QuestionCircleOutlined />}
                                onClick={() => setHelpModalVisible(true)}
                                style={{ color: '#8B5CF6' }}
                            >
                                {t('wms.zipcodeMapGeofence.shortcuts.helpButton')}
                            </Button>
                    </div>
                )}
                {isLoaded && !loading && !error && (
                    <div className="absolute bottom-4 left-4 z-20 flex flex-col items-start gap-2">
                        {/* 图例控制面板：控制 zipcode 边界与各状态围栏的显隐 */}
                        <div className="bg-white rounded-lg shadow-md px-3 py-2 flex flex-col gap-1">
                            <Checkbox
                                checked={showZipcodeBoundary}
                                disabled={hasDrawingInProgress}
                                onChange={(e) => setShowZipcodeBoundary(e.target.checked)}
                            >
                                <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#8B5CF6' }} />
                                {t('wms.zipcodeMapGeofence.legend.zipcodeBoundary')}
                            </Checkbox>
                            <Checkbox
                                checked={showPendingGeofences}
                                onChange={(e) => setShowPendingGeofences(e.target.checked)}
                            >
                                <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#FACC15' }} />
                                {t('wms.zipcodeMapGeofence.legend.pendingGeofence')}
                            </Checkbox>
                            <Checkbox
                                checked={showApprovedGeofences}
                                onChange={(e) => setShowApprovedGeofences(e.target.checked)}
                            >
                                <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#3B82F6' }} />
                                {t('wms.zipcodeMapGeofence.legend.approvedGeofence')}
                            </Checkbox>
                            <Checkbox
                                checked={showEffectiveGeofences}
                                onChange={(e) => setShowEffectiveGeofences(e.target.checked)}
                            >
                                <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#22C55E' }} />
                                {t('wms.zipcodeMapGeofence.legend.effectiveGeofence')}
                            </Checkbox>
                            <Checkbox
                                checked={showInactiveGeofences}
                                onChange={(e) => setShowInactiveGeofences(e.target.checked)}
                            >
                                <span className="inline-block w-3 h-3 rounded-sm mr-1 align-middle" style={{ backgroundColor: '#EF4444' }} />
                                {t('wms.zipcodeMapGeofence.legend.inactiveGeofence')}
                            </Checkbox>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                type={mapTypeId === 'roadmap' ? 'primary' : 'default'}
                                onClick={() => handleMapTypeChange('roadmap')}
                                style={mapTypeId !== 'roadmap' ? { color: '#8B5CF6' } : undefined}
                            >
                                {t('wms.zipcodeMapGeofence.vectorMap')}
                            </Button>
                            <Button
                                type={mapTypeId === 'satellite' ? 'primary' : 'default'}
                                onClick={() => handleMapTypeChange('satellite')}
                                style={mapTypeId !== 'satellite' ? { color: '#8B5CF6' } : undefined}
                            >
                                {t('wms.zipcodeMapGeofence.satelliteMap')}
                            </Button>
                        </div>
                    </div>
                )}
                {(!isLoaded || loading) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                        <Spin size="large" />
                    </div>
                )}
                {error && !loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
                        <div className="text-red-500">{error}</div>
                    </div>
                )}
                {isLoaded && (
                    <GoogleMap
                        key={`google-map-${drawVersion}`}
                        mapContainerStyle={{ width: '100%', height: '100%' }}
                        center={defaultCenter}
                        zoom={4}
                        options={getMapOptions(drawMode, mapTypeId)}
                        onLoad={onMapLoad}
                        onUnmount={onMapUnmount}
                        onClick={handleMapClick}
                        onMouseMove={handleMouseMove}
                        onRightClick={handleRightClick}
                    >
                        {boundaryPolygons}
                        {geofencePolygons}
                        {geofenceLabels}
                        {/* geofenceVertexMarkers */}
                        {drawPointMarkers}
                        {drawConnectionLine}
                        {drawPreviewLines}
                        {drawPolygonFill}
                        {drawnGeofencePolygonsRender}
                    </GoogleMap>
                )}
                {/* 围栏列表抽屉：挂载在地图容器内部，保证全屏时仍可见 */}
                <div
                    className={`${embedded ? 'absolute' : 'fixed'} right-0 bg-white shadow-lg transition-all duration-300 rounded-l-lg ${
                        listDrawerVisible ? 'w-[30%]' : 'w-0'
                    }`}
                    style={{
                        // embedded 时用 absolute 定位（Drawer 动画期间 fixed 定位基准会跳变），
                        // top 相应上移，避免底缘超出父容器被 overflow-hidden 裁剪
                        top: embedded ? '55%' : '60%',
                        transform: 'translateY(-50%)',
                        height: '85%',
                        zIndex: 9999,
                    }}
                >
                    {/* 收缩/展开按钮 */}
                    <button
                        onClick={() => setListDrawerVisible(!listDrawerVisible)}
                        className="absolute left-0 top-1/2 -translate-x-full -translate-y-1/2 bg-white shadow-md rounded-l-md px-2 py-4 hover:bg-gray-50 transition-colors"
                        style={{ zIndex: 10000 }}
                    >
                        {listDrawerVisible ? <RightOutlined /> : <LeftOutlined />}
                    </button>

                    {/* 抽屉内容 */}
                    {listDrawerVisible && (
                        <div className="h-full flex flex-col p-4 overflow-hidden">
                            <h3 className="text-lg font-semibold mb-4">
                                ZIP Code: {zipcode}
                            </h3>
                            <div className="flex-1 overflow-auto">
                                <Table
                                    columns={columns}
                                    dataSource={geofenceListData}
                                    rowKey={(record) => record.id?.toString() || `temp-${Math.random()}`}
                                    loading={geofenceLoading}
                                    pagination={{
                                        current: tablePagination.current,
                                        pageSize: tablePagination.pageSize,
                                        showSizeChanger: true,
                                        showTotal: (total) => `共 ${total} 条`,
                                        pageSizeOptions: ['10', '20', '50', '100'],
                                        onChange: (page, pageSize) => {
                                            setTablePagination({ current: page, pageSize });
                                        },
                                    }}
                                    scroll={{ x: 1000, y: 'calc(60vh + 10px)' }}
                                    size="small"
                                    bordered={false}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 围栏信息弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.form.title')}
                open={drawerVisible}
                onCancel={handleDrawerClose}
                getContainer={() => mapContainerRef.current || document.body}
                zIndex={11000}
                footer={[
                    <Button key="cancel" onClick={handleDrawerClose}>
                        {t('wms.zipcodeMapGeofence.form.cancelButton')}
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleDrawerSubmit}>
                        {t('wms.zipcodeMapGeofence.form.submitButton')}
                    </Button>,
                ]}
                width={500}
            >
                <Form
                    form={form}
                    layout="vertical"
                    autoComplete="off"
                >
                    <Form.Item
                        label={t('wms.zipcodeMapGeofence.form.nameLabel') as string}
                        name="name"
                        rules={[
                            { required: true, message: t('wms.zipcodeMapGeofence.form.nameRequired') as string },
                            { max: 10, message: t('wms.zipcodeMapGeofence.form.nameMaxLength') as string }
                        ]}
                    >
                        <Input placeholder={t('wms.zipcodeMapGeofence.form.namePlaceholder') as string} maxLength={10} />
                    </Form.Item>

                    <Form.Item
                        label={t('wms.zipcodeMapGeofence.form.descriptionLabel') as string}
                        name="description"
                        rules={[{ required: true, message: t('wms.zipcodeMapGeofence.form.descriptionRequired') as string }]}
                    >
                        <Select
                            placeholder={t('wms.zipcodeMapGeofence.form.descriptionPlaceholder') as string}
                            getPopupContainer={(triggerNode) => triggerNode.parentElement || document.body}
                        >
                            <Select.Option value="PO_BOX_OR_POST_OFFICE">USPS</Select.Option>
                            <Select.Option value="MILITARY">MILITARY</Select.Option>
                            <Select.Option value="BLACK_FENCE_LIST">OTHER</Select.Option>
                        </Select>
                    </Form.Item>

                    {descriptionValue === 'BLACK_FENCE_LIST' && (
                        <Form.Item
                            label={t('wms.zipcodeMapGeofence.form.otherDescriptionLabel') as string}
                            name="otherDescription"
                            rules={[
                                { required: true, message: t('wms.zipcodeMapGeofence.form.otherDescriptionRequired') as string },
                                { max: 20, message: t('wms.zipcodeMapGeofence.form.otherDescriptionMaxLength') as string }
                            ]}
                        >
                            <Input placeholder={t('wms.zipcodeMapGeofence.form.otherDescriptionPlaceholder') as string} maxLength={20} />
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* 审核弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.audit.hint')}
                open={auditModalVisible}
                onCancel={handleAuditCancel}
                getContainer={() => mapContainerRef.current || document.body}
                zIndex={11000}
                footer={[
                    <Button key="reject" danger onClick={handleAuditReject} loading={auditGeofenceLoading}>
                        {t('wms.zipcodeMapGeofence.audit.rejectButton')}
                    </Button>,
                    <Button key="approve" type="primary" onClick={handleAuditApprove} loading={auditGeofenceLoading}>
                        {t('wms.zipcodeMapGeofence.audit.approveButton')}
                    </Button>,
                ]}
                width={500}
            >
                <div className="py-4">
                    <div className="mb-3 px-3 py-2 rounded bg-red-50 text-red-600 font-medium">
                        {t('wms.zipcodeMapGeofence.audit.deliveredHint', { count: currentAuditRecord?.deliveredTotal ?? 0 })}
                    </div>
                    <div className="space-y-2 text-gray-600">
                        <div className="flex items-start">
                            <span className="font-medium mr-2">{t('wms.zipcodeMapGeofence.audit.approveHint')}</span>
                            <span>{t('wms.zipcodeMapGeofence.audit.approveDesc')}</span>
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium mr-2">{t('wms.zipcodeMapGeofence.audit.rejectHint')}</span>
                            <span>{t('wms.zipcodeMapGeofence.audit.rejectDesc')}</span>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* 删除确认弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.delete.title')}
                open={deleteModalVisible}
                onCancel={handleDeleteCancel}
                getContainer={() => mapContainerRef.current || document.body}
                zIndex={11000}
                footer={[
                    <Button key="cancel" onClick={handleDeleteCancel} disabled={deleteGeofenceLoading}>
                        {t('wms.zipcodeMapGeofence.delete.cancelButton')}
                    </Button>,
                    <Button key="delete" type="primary" danger onClick={handleDeleteConfirm} loading={deleteGeofenceLoading}>
                        {t('wms.zipcodeMapGeofence.delete.deleteButton')}
                    </Button>,
                ]}
                width={400}
            >
                <div className="py-4">
                    <p className="text-gray-700">{t('wms.zipcodeMapGeofence.delete.confirmMessage')}</p>
                </div>
            </Modal>

            {/* 帮助/快捷键弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.shortcuts.helpButton')}
                open={helpModalVisible}
                onCancel={() => setHelpModalVisible(false)}
                getContainer={() => mapContainerRef.current || document.body}
                zIndex={11000}
                footer={[
                    <Button key="close" type="primary" onClick={() => setHelpModalVisible(false)}>
                        {t('wms.zipcodeMapGeofence.shortcuts.closeButton')}
                    </Button>,
                ]}
                width={500}
            >
                <div className="py-4 space-y-4">
                    <div>
                        <div className="text-sm font-medium text-gray-800 mb-2">
                            {t('wms.zipcodeMapGeofence.shortcuts.title')}
                        </div>
                        <ul className="space-y-3 text-gray-700">
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                    <ZoomInOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.doubleClickZoom')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                    <VerticalAlignMiddleOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.wheelZoom')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                    <BorderOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.drawGeofenceSteps')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                    <AimOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.clickIdToLocate')}</span>
                            </li>
                        </ul>
                    </div>
                    {/* <div>
                        <div className="text-sm font-medium text-gray-800 mb-2">
                            {t('wms.zipcodeMapGeofence.shortcuts.operationTitle')}
                        </div>
                        <ul className="space-y-3 text-gray-700">
                         
                            <li className="flex items-start gap-2">
                                <span className="text-blue-500 mt-0.5 flex-shrink-0">
                                    <SearchOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.searchAddressToLocate')}</span>
                            </li>
                        </ul>
                    </div> */}
                    <div>
                        <div className="text-sm font-medium text-gray-800 mb-2">
                            {t('wms.zipcodeMapGeofence.shortcuts.noticeTitle')}
                        </div>
                        <ul className="space-y-3 text-gray-700">
                          
                            <li className="flex items-start gap-2">
                                <span className="text-yellow-500 mt-0.5 flex-shrink-0">
                                    <ExclamationCircleOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.noticeMinAnchors')}</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-yellow-500 mt-0.5 flex-shrink-0">
                                    <ExclamationCircleOutlined />
                                </span>
                                <span>{t('wms.zipcodeMapGeofence.shortcuts.noticeNoZoomDuringDraw')}</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </Modal>

            {/* 围栏信息查看弹窗 */}
            <Modal
                title={t('wms.zipcodeMapGeofence.info.title')}
                open={geofenceInfoModalVisible}
                onCancel={handleGeofenceInfoModalClose}
                getContainer={() => mapContainerRef.current || document.body}
                zIndex={11000}
                footer={[
                    <Button key="close" type="primary" onClick={handleGeofenceInfoModalClose}>
                        {t('wms.zipcodeMapGeofence.info.closeButton')}
                    </Button>,
                ]}
                width={500}
            >
                {selectedGeofence && (
                    <div className="space-y-4">
                        <div className="flex items-start">
                            <span className="font-medium text-gray-600 w-32">{t('wms.zipcodeMapGeofence.info.id')}:</span>
                            <span className="text-gray-900">{selectedGeofence.id || '-'}</span>
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium text-gray-600 w-32">{t('wms.zipcodeMapGeofence.info.geofenceName')}:</span>
                            <span className="text-gray-900">{selectedGeofence.geofenceName || '-'}</span>
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium text-gray-600 w-32">{t('wms.zipcodeMapGeofence.info.description')}:</span>
                            <span className="text-gray-900">
                                {getDescriptionLabel(selectedGeofence.descriptionType, selectedGeofence.descriptionOther)}
                            </span>
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium text-gray-600 w-32">{t('wms.zipcodeMapGeofence.info.status')}:</span>
                            {(() => {
                                const config = getGeofenceStatusConfig(selectedGeofence.status, t);
                                return (
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${config.color}`}>
                                        {config.text}
                                    </span>
                                );
                            })()}
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium text-gray-600 w-32">{t('wms.zipcodeMapGeofence.info.creator')}:</span>
                            <span className="text-gray-900">{selectedGeofence.creatorName || '-'}</span>
                        </div>
                        <div className="flex items-start">
                            <span className="font-medium text-gray-600 w-32">{t('wms.zipcodeMapGeofence.info.createdAt')}:</span>
                            <span className="text-gray-900">
                                <span className="text-sm">{selectedGeofence.createdAt ? formatTimestampToDateTime(selectedGeofence.createdAt) : '--'}</span>
                            </span>
                        </div>
                    </div>
                )}
            </Modal>

        </div>
    );
};
